<template>
  <div>
    <div v-if="isLoading" class="flex justify-center items-center py-16">
      <Loading />
    </div>
    <p v-else-if="storesCompletion && !trainingModules.length" class="py-8 text-center text-sm text-gray-400">
      {{ t("no_modules") }}
    </p>
    <template v-else-if="storesCompletion">
      <div class="mb-4">
        <p class="text-sm text-gray-500">{{ t("hint") }}</p>
        <div class="mt-2 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-gray-500">
          <span v-for="item in legend" :key="item.level" class="inline-flex items-center gap-1.5">
            <span class="h-2 w-2 rounded-full" :class="item.dotClass"></span>
            {{ item.label }}
          </span>
          <span class="text-gray-400">{{ t("legend_none") }}</span>
        </div>
      </div>
      <Table :columns="columns" :data="sortedRows" :order="order" :no-data-msg="t('no_data')" @sort="handleSort">
        <template #name="{ row }">
          <span class="font-semibold text-gray-900">{{ row.name }}</span>
        </template>
        <template
          v-for="trainingModule in trainingModules"
          #[moduleField(trainingModule.id)]="{ row }"
          :key="trainingModule.id"
        >
          <CompletionBadge
            :percent="row[moduleField(trainingModule.id)]"
            clickable
            @select="openCell(row, trainingModule)"
          />
        </template>
      </Table>
    </template>

    <NotTrainedSellersModal :open="isModalOpen" :selection="selection" @close="isModalOpen = false" />
  </div>
</template>

<script>
import { Loading, Table } from "@estaly/ui";
import { mapActions, mapGetters, mapState } from "vuex";
import { useI18n } from "vue-i18n";
import CompletionBadge from "./CompletionBadge.vue";
import NotTrainedSellersModal from "./NotTrainedSellersModal.vue";
import { LEVEL_CLASSES, TRAINED_THRESHOLD, buildRows, moduleField, sortRows } from "@/utils/trainingCompletion";

export default {
  name: "StoresTrainingCompletion",
  components: { CompletionBadge, Loading, NotTrainedSellersModal, Table },
  setup() {
    const { t } = useI18n();
    return { t };
  },
  data() {
    return {
      isLoading: false,
      // Defaults to the first module, most trained stores first
      sortField: null,
      sortDirection: "desc",
      isModalOpen: false,
      selection: null,
    };
  },
  computed: {
    ...mapGetters("auth", ["accessibleStoreIds"]),
    ...mapState("trainingModules", ["storesCompletion"]),
    trainingModules() {
      return this.storesCompletion?.trainingModules || [];
    },
    rows() {
      return buildRows(this.storesCompletion?.stores || [], this.trainingModules);
    },
    effectiveSortField() {
      return this.sortField || (this.trainingModules.length ? moduleField(this.trainingModules[0].id) : "name");
    },
    sortedRows() {
      return sortRows(this.rows, this.effectiveSortField, this.sortDirection);
    },
    order() {
      return `${this.sortDirection === "asc" ? "" : "-"}${this.effectiveSortField}`;
    },
    columns() {
      return [
        // The store name takes the remaining space, module columns keep a fixed width for the badges
        { label: this.t("store"), field: "name", slot: "name", sortable: true, widthClass: "w-full" },
        ...this.trainingModules.map((trainingModule) => ({
          label: trainingModule.title,
          field: moduleField(trainingModule.id),
          slot: moduleField(trainingModule.id),
          sortable: true,
          numeric: true,
          widthClass: "w-48",
        })),
      ];
    },
    legend() {
      return [
        { level: "trained", label: this.t("legend_trained", { threshold: TRAINED_THRESHOLD }) },
        { level: "in_progress", label: this.t("legend_in_progress", { max: TRAINED_THRESHOLD - 1 }) },
        { level: "not_started", label: this.t("legend_not_started") },
      ].map((item) => ({ ...item, dotClass: LEVEL_CLASSES[item.level].dot }));
    },
  },
  // Data already loaded (e.g. back on the tab) stays displayed while it refreshes
  async created() {
    this.isLoading = !this.storesCompletion;
    try {
      const response = await this.fetchStoresCompletion(this.accessibleStoreIds);
      if (!response?.success) {
        this.notify({ category: "simple", type: "error", title: this.t("load_error") });
      }
    } finally {
      this.isLoading = false;
    }
  },
  methods: {
    ...mapActions("notifications", ["notify"]),
    ...mapActions("trainingModules", ["fetchStoresCompletion"]),
    moduleField,
    handleSort(column) {
      if (this.effectiveSortField === column.field) {
        this.sortDirection = this.sortDirection === "asc" ? "desc" : "asc";
      } else {
        this.sortField = column.field;
        this.sortDirection = column.field === "name" ? "asc" : "desc";
      }
    },
    openCell(row, trainingModule) {
      this.selection = {
        storeName: row.name,
        moduleTitle: trainingModule.title,
        cell: row.cells[trainingModule.id],
      };
      this.isModalOpen = true;
    },
  },
};
</script>

<i18n src="./StoresTrainingCompletion.i18n.json"></i18n>
